import { useState, useEffect, lazy, Suspense } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { trackEvent } from "@/lib/analytics";
const InstallGuideDialog = lazy(() => import("@/components/InstallGuideDialog"));
const Footer = lazy(() => import("@/components/Footer"));
const LandingChat = lazy(() => import("@/components/chat/LandingChat").then(m => ({ default: m.LandingChat })));
import { useNavigate } from "react-router-dom";
import BrandText from "@/components/BrandText";
import AppLogo from "@/components/AppLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  Building, Lightbulb, Trophy, Rocket, CheckCircle, Shield, Clock,
  MessageSquare, BarChart3, File, Handshake, User, GraduationCap, Scale,
  Zap, Download, ArrowRight, HelpCircle, Star, Users, Search, Mail,
  MapPin, Globe, Quote
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { betaAccessPath } from "@/lib/beta-navigation";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const } }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" as const } },
};

export default function Landing() {
  const { canInstall, isInstalled, install } = usePWAInstall();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  usePageMeta({
    title: "CreaFinancia – Herramientas de orientación con IA · Beta gratuita",
    description: "Crea tu empresa con IA: análisis de viabilidad gratis, orientación para crear tu SL y radar de ayudas ENISA y CDTI gratis durante la beta.",
    canonical: "/",
    keywords: "crear empresa España, constitución SL beta gratuita, análisis viabilidad gratis, ayudas emprendedores, subvenciones ENISA CDTI, plan de negocio IA, DUE CIRCE",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "¿Qué límites tiene la beta gratuita?", "acceptedAnswer": { "@type": "Answer", "text": "Cada nuevo usuario puede crear 2 planes de negocio, 2 planes financieros y una empresa en total; borrar un recurso no recupera el cupo. Cada usuario dispone de 20 mensajes diarios compartidos entre los chats, con hasta 5 consultas con imágenes dentro de esos 20, y 2 generaciones de IA por documento de plan de negocio o financiero. Los cupos diarios se reinician a las 00:00 UTC; descargar PDFs ya generados no consume IA." } },
          { "@type": "Question", "name": "¿Qué es CreaFinancia y cómo funciona?", "acceptedAnswer": { "@type": "Answer", "text": "CreaFinancia es una plataforma online que utiliza inteligencia artificial para ayudarte a crear tu empresa en España. Analiza la viabilidad de tu idea de negocio, te guía paso a paso en la constitución de tu sociedad y te conecta con ayudas públicas y subvenciones adaptadas a tu perfil." } },
          { "@type": "Question", "name": "¿El análisis de viabilidad es realmente gratuito?", "acceptedAnswer": { "@type": "Answer", "text": "Sí. Necesitas una cuenta gratuita para realizar el análisis. Durante la beta, los tres niveles son gratuitos, sin tarjeta ni nuevos cobros. El uso de IA está sujeto a los cupos de la beta y el acceso no se convertirá automáticamente en una suscripción de pago." } },
          { "@type": "Question", "name": "¿Cómo me ayuda la IA a constituir mi empresa?", "acceptedAnswer": { "@type": "Answer", "text": "La IA ofrece orientación sobre formas jurídicas, borradores de plan de negocio y guías DUE/CIRCE. Tú realizas los trámites por los canales oficiales; la aplicación no es un Punto PAE acreditado ni garantiza plazos o resultados." } },
          { "@type": "Question", "name": "¿Qué ayudas y subvenciones puedo encontrar?", "acceptedAnswer": { "@type": "Answer", "text": "CreaFinancia busca y filtra ayudas ENISA, CDTI, subvenciones autonómicas, nacionales y europeas adaptadas a tu perfil empresarial, sector y ubicación. Ofrece información orientativa sobre requisitos y plazos, que debes confirmar en la convocatoria oficial. ENISA ofrece préstamos participativos, no subvenciones a fondo perdido. La compatibilidad estimada no acredita elegibilidad ni garantiza la concesión." } },
          { "@type": "Question", "name": "¿Es seguro introducir los datos de mi proyecto?", "acceptedAnswer": { "@type": "Answer", "text": "Aplicamos medidas de seguridad y controles de acceso, sin garantía de seguridad absoluta. Los proveedores de alojamiento, correo e IA pueden tratar datos para operar las herramientas según la Política de Privacidad. No vendemos tus datos; evita aportar información personal innecesaria o sensible." } },
          { "@type": "Question", "name": "¿Puedo usar CreaFinancia desde el móvil?", "acceptedAnswer": { "@type": "Answer", "text": "Sí, CreaFinancia es una aplicación web progresiva (PWA) totalmente optimizada para móvil. Puedes instalarla en tu smartphone como una app nativa y acceder a todas las funcionalidades desde cualquier dispositivo y lugar." } }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://creafinancia.es/" },
          { "@type": "ListItem", "position": 2, "name": "Constitución de Empresas", "item": "https://creafinancia.es/constitution-info" },
          { "@type": "ListItem", "position": 3, "name": "Ayudas y Subvenciones", "item": "https://creafinancia.es/aids-info" },
          { "@type": "ListItem", "position": 4, "name": "Centro de Ayuda", "item": "https://creafinancia.es/help-center" }
        ]
      }
    ]
  });

  useEffect(() => { trackEvent("landing_view"); }, []);

  const handleStartAnalysis = () => { trackEvent("cta_click", { cta: "start_analysis" }); navigate(betaAccessPath(1, isAuthenticated)); };
  const handleLogin = () => { trackEvent("cta_click", { cta: "login" }); navigate("/login"); };

  const handleInstallClick = () => {
    if (isInstalled) {
      // En escritorio, mostrar mensaje de app ya instalada
      import("sonner").then(({ toast }) => toast.info("La app ya está instalada en tu dispositivo"));
      return;
    }
    if (canInstall) {
      install();
    } else {
      setShowInstallGuide(true);
    }
  };

  return (
    <div className="gradient-hero font-body min-h-screen">
      {/* Navigation Header */}
      <header className="glass shadow-soft sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-[66px]">
            <div className="flex items-center min-w-0 flex-1">
              <div className="flex items-center space-x-3 min-w-0">
                <AppLogo size="responsive" style={{ alignSelf: 'center' }} priority />
                <BrandText className="text-2xl sm:text-2xl md:text-3xl truncate self-center leading-none" />
              </div>
            </div>
            <div className="flex items-center flex-shrink-0 gap-1 sm:gap-2">
              <Button variant="ghost" onClick={() => navigate("/contact")} size="sm" className="hidden sm:inline-flex h-9 px-3 text-xs font-medium rounded-lg text-muted-foreground hover:text-foreground transition-colors">
                <Mail size={14} className="mr-1.5" />
                Contacto
              </Button>
              <Button variant="outline" onClick={handleInstallClick} size="sm" className="hidden sm:inline-flex h-9 px-3 text-xs font-medium rounded-lg border-primary/30 text-primary hover:text-secondary hover:border-secondary/30 hover:bg-secondary/10 transition-colors">
                <Download size={14} className="mr-1.5" />
                {isInstalled ? "App Instalada" : "Instalar App"}
              </Button>
              {!isInstalled && (
                <Button variant="ghost" onClick={handleInstallClick} size="icon" className="sm:hidden text-primary hover:text-secondary hover:bg-secondary/10 h-9 w-9" aria-label="Instalar App">
                  <Download size={18} />
                </Button>
              )}
              <Button variant="ghost" onClick={handleLogin} size="icon" className="text-muted-foreground hover:text-foreground transition-colors h-9 w-9 sm:h-10 sm:w-10" aria-label="Iniciar sesión">
                <User size={20} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main>
      {/* Hero Section */}
      <section aria-label="Sección principal" className="relative py-8 sm:py-8 md:py-12 lg:py-16 overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl hidden sm:block" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-3xl hidden sm:block" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="space-y-4 sm:space-y-8 text-center"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.h1 variants={fadeUp} custom={0} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-extrabold text-foreground leading-[1.1] tracking-tight">
              CreaFinancia
            </motion.h1>
            
            <motion.p variants={fadeUp} custom={1} className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Explora gratis herramientas de IA para analizar tu idea, guiarte sobre la constitución de una empresa y encontrar ayudas públicas. No sustituye el asesoramiento profesional ni realiza trámites en tu nombre.
            </motion.p>
            
            <motion.div variants={fadeUp} custom={2} className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-center justify-center">
              <Button size="lg" onClick={handleStartAnalysis} className="gradient-primary text-primary-foreground shadow-primary hover:shadow-xl hover:opacity-95 transition-all duration-300 h-11 sm:h-12 px-5 sm:px-6 text-sm sm:text-base font-semibold rounded-xl group">
                <Rocket className="mr-2 group-hover:rotate-12 transition-transform" size={16} />
                Analizar mi Idea
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
              </Button>
              <Button size="lg" variant="outline" onClick={handleLogin} className="h-11 sm:h-12 px-5 sm:px-6 text-sm sm:text-base font-medium rounded-xl border-border/60 shadow-lg hover:bg-muted hover:text-foreground hover:shadow-2xl transition-all duration-300">
                Ya tengo cuenta
              </Button>
            </motion.div>
            
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap items-center justify-center gap-3 sm:gap-5 pt-1 sm:pt-2">
              {[
                { icon: CheckCircle, text: "100% Online" },
                { icon: Shield, text: "Beta gratuita" },
                { icon: Clock, text: "Orientación paso a paso" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <item.icon className="text-secondary" size={13} />
                  <span className="text-muted-foreground text-xs sm:text-sm">{item.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Metrics */}
      <section id="metricas" aria-label="Métricas de confianza" className="scroll-mt-20 py-6 sm:py-10 bg-card border-y border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {[
              { icon: BarChart3, value: "Viabilidad", label: "Análisis orientativo con IA", color: "text-primary" },
              { icon: Building, value: "Constitución", label: "Guías de trámites oficiales", color: "text-secondary" },
              { icon: Search, value: "Ayudas", label: "Consulta de fuentes públicas", color: "text-accent" },
              { icon: Star, value: "Beta gratuita", label: "Proyecto experimental", color: "text-primary" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                className="text-center space-y-1.5"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
              >
                <stat.icon className={`mx-auto ${stat.color} opacity-70`} size={20} />
                <p className="text-2xl sm:text-3xl font-display font-extrabold text-foreground tracking-tight">{stat.value}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Asistente IA - Presentación */}
      <section aria-label="Asistente IA" className="py-6 sm:py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="relative cursor-pointer"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Card className="shadow-xl border border-border/50 bg-card/80 backdrop-blur-sm rounded-2xl overflow-hidden">
              <CardContent className="p-4 sm:p-7">
                <div className="flex items-center space-x-2.5 mb-2 pb-2 border-b border-border/50">
                  <AppLogo size="card" priority />
                  <div>
                    <h2 className="font-display font-semibold text-foreground text-sm">Asistente <BrandText className="inline-block text-sm" /></h2>
                    <p className="text-xs text-secondary flex items-center gap-1"><span className="w-1.5 h-1.5 bg-secondary rounded-full animate-pulse"></span>En línea</p>
                  </div>
                </div>
                <div>
                  <p className="text-foreground/80 text-sm sm:text-base leading-relaxed text-center">
                    ¡Hola! Soy tu Asistente especializado en <strong className="text-primary">creación de empresas</strong> y <strong className="text-secondary">búsqueda de ayudas públicas</strong>. Te acompaño <strong>paso a paso</strong> durante todo el proceso.
                  </p>
                  <p className="mt-3 text-foreground/80 text-sm sm:text-base leading-relaxed text-center bg-primary/5 rounded-xl p-3 sm:p-4">
                    Puedes consultar nuestras guías de <strong>CIRCE</strong> y del <strong>DUE</strong> para comprender los pasos oficiales. <strong>CreaFinancia</strong> aporta orientación experimental con IA: no es un Punto PAE acreditado, no coordina notarías ni presenta altas o solicitudes en tu nombre. Los plazos y requisitos dependen de los organismos competentes y de tu situación.
                  </p>
                </div>
              </CardContent>
            </Card>
            <div className="absolute -top-3 -right-3 w-20 h-20 bg-accent/10 rounded-2xl blur-xl" />
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary/10 rounded-full blur-xl" />
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" aria-label="Servicios de CreaFinancia" className="scroll-mt-20 py-10 sm:py-20 md:py-28 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center space-y-3 sm:space-y-4 mb-8 sm:mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.div variants={fadeUp} custom={0}>
              <Badge variant="outline" className="border-primary/20 text-primary-accessible">Herramientas beta</Badge>
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-4xl md:text-5xl font-display font-extrabold text-foreground tracking-tight">
              Tres niveles, un objetivo
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Todos los niveles gratis durante la beta · Sin tarjeta ni nuevos cobros
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Nivel 1 */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
              <Card className="h-full card-elevated bg-card border border-border/50 rounded-2xl overflow-hidden group">
                <CardContent className="p-4 sm:p-8 flex flex-col h-full">
                  <div className="flex items-center gap-3 mb-4 sm:mb-5">
                    <div className="w-9 h-9 sm:w-11 sm:h-11 bg-level-1/10 rounded-xl flex items-center justify-center group-hover:bg-level-1/15 transition-colors flex-shrink-0">
                      <Lightbulb className="text-level-1" size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] sm:text-xs font-semibold text-level-1-accessible uppercase tracking-wider">Nivel 1</p>
                      <h3 className="text-base sm:text-lg font-display font-bold text-foreground truncate">Análisis de Viabilidad</h3>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm mb-6 leading-relaxed">Análisis completo de viabilidad empresarial con IA. Registro gratuito.</p>
                  <ul className="space-y-2.5 mb-8 flex-1">
                    {["Scoring preliminar ENISA/CDTI", "Recomendación forma jurídica", "Estimación costes constitución", "Informe PDF profesional", "Análisis mercado y competencia", "Chat IA 24/7"].map((item, i) => (
                      <li key={i} className="flex items-start gap-2"><CheckCircle className="text-level-1 mt-0.5 flex-shrink-0" size={14} /><span className="text-sm text-muted-foreground">{item}</span></li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between pt-4 border-t border-border/50">
                    <span className="text-2xl font-display font-extrabold text-foreground">Gratis</span>
                    <Button onClick={handleStartAnalysis} className="gradient-level-1 text-level-1-foreground rounded-xl shadow-soft hover:opacity-95">Empezar</Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Nivel 2 */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
              <Card className="h-full card-elevated border-2 border-level-2/30 rounded-2xl overflow-hidden relative group">
                <div className="absolute top-0 left-0 right-0 h-1 gradient-level-2" />
                <CardContent className="p-4 sm:p-8 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-4 sm:mb-5">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-11 sm:h-11 bg-level-2/10 rounded-xl flex items-center justify-center group-hover:bg-level-2/15 transition-colors flex-shrink-0">
                        <Building className="text-level-2" size={18} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] sm:text-xs font-bold text-level-2-accessible uppercase tracking-wider">Nivel 2</p>
                        <h3 className="text-base sm:text-lg font-display font-bold text-foreground">Constitución</h3>
                      </div>
                    </div>
                    <Badge className="bg-level-1/10 border-level-1/20 text-[11px] font-bold text-level-1-accessible">ACCESO BETA</Badge>
                  </div>
                  <p className="text-muted-foreground text-sm mb-6 leading-relaxed">Orientación sobre constitución y borrador de Plan de Negocio con IA. Tú realizas los trámites oficiales.</p>
                  <ul className="space-y-2.5 mb-8 flex-1">
                    {["Plan de Negocio con IA", "Guía DUE/CIRCE paso a paso", "Orientación sobre la cita notarial", "Chat + Visión IA", "Guía del Registro Mercantil"].map((item, i) => (
                      <li key={i} className="flex items-start gap-2"><CheckCircle className="text-level-2 mt-0.5 flex-shrink-0" size={14} /><span className="text-sm text-muted-foreground">{item}</span></li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between pt-4 border-t border-border/50">
                    <div>
                      <Badge className="bg-level-1/15 border-level-1/30 text-[10px] font-bold text-level-1-accessible mb-1">Beta gratuita</Badge>
                       <div><span className="text-2xl font-display font-extrabold text-foreground">Gratis</span><span className="text-xs text-muted-foreground ml-1"></span></div>
                      <p className="text-[10px] text-muted-foreground">{"\n"}</p>
                    </div>
                    <Button className="gradient-level-2 text-level-2-foreground rounded-xl shadow-soft hover:opacity-95" onClick={() => navigate(betaAccessPath(2, isAuthenticated))}>Acceder</Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Nivel 3 */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={2}>
              <Card className="h-full card-elevated bg-card border border-border/50 rounded-2xl overflow-hidden group">
                <CardContent className="p-4 sm:p-8 flex flex-col h-full">
                  <div className="flex items-center gap-3 mb-4 sm:mb-5">
                    <div className="w-9 h-9 sm:w-11 sm:h-11 bg-level-3/10 rounded-xl flex items-center justify-center group-hover:bg-level-3/15 transition-colors flex-shrink-0">
                      <Trophy className="text-level-3" size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] sm:text-xs font-bold text-level-3-accessible uppercase tracking-wider">Nivel 3</p>
                      <h3 className="text-base sm:text-lg font-display font-bold text-foreground">Buscador de Ayudas</h3>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm mb-6 leading-relaxed">Búsqueda y orientación sobre ayudas públicas con IA, sin tramitación ni garantía de concesión.</p>
                  <ul className="space-y-2.5 mb-8 flex-1">
                    {["Radar de ayudas 24/7", "Buscador nacional + UE (+1000 ayudas)", "Dashboard personalizado", "Plan Financiero con IA", "Guía memorias ENISA", "Chat + Visión IA", "Alertas personalizadas"].map((item, i) => (
                      <li key={i} className="flex items-start gap-2"><CheckCircle className="text-level-3 mt-0.5 flex-shrink-0" size={14} /><span className="text-sm text-muted-foreground">{item}</span></li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between pt-4 border-t border-border/50">
                    <div>
                      <Badge className="bg-level-1/15 border-level-1/30 text-[10px] font-bold text-level-1-accessible mb-1">Beta gratuita</Badge>
                      <div><span className="text-2xl font-display font-extrabold text-foreground">Gratis</span><span className="text-xs text-muted-foreground ml-1"></span></div>
                    </div>
                    <Button className="gradient-level-3 text-level-3-foreground rounded-xl shadow-soft hover:opacity-95" onClick={() => navigate(betaAccessPath(3, isAuthenticated))}>Acceder</Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="proceso" aria-label="Cómo funciona el proceso" className="scroll-mt-20 py-10 sm:py-20 md:py-28 gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center space-y-3 sm:space-y-4 mb-10 sm:mb-16"
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.div variants={fadeUp} custom={0}>
              <Badge variant="outline" className="border-primary/20 text-primary">Proceso</Badge>
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-4xl md:text-5xl font-display font-extrabold text-foreground tracking-tight">
              ¿Cómo Funciona?
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Tu asistente de IA especializado, disponible <span className="text-primary font-semibold">24/7</span>
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {[
              { icon: MessageSquare, title: "Asistente IA", desc: "Chat inteligente disponible siempre para resolver tus dudas", color: "primary" },
              { icon: BarChart3, title: "Análisis Guiado", desc: "Te ayudamos a evaluar la viabilidad de tu idea", color: "secondary" },
              { icon: File, title: "Guía DUE/Ayudas", desc: "Asistencia paso a paso en formularios oficiales", color: "accent" },
              { icon: Handshake, title: "Acompañamiento", desc: "Soporte continuo desde constitución hasta ayudas", color: "primary" }
            ].map((step, index) => (
              <motion.div 
                key={index} 
                className="text-center space-y-4"
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={index}
              >
              <div className="relative mx-auto w-12 h-12 sm:w-16 sm:h-16">
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-${step.color}/10 rounded-xl sm:rounded-2xl flex items-center justify-center`}>
                    <step.icon className={`text-${step.color}`} size={22} />
                  </div>
                  <div className="absolute -top-1.5 -left-1.5 sm:-top-2 sm:-left-2 w-6 h-6 sm:w-7 sm:h-7 bg-card border border-border rounded-md sm:rounded-lg flex items-center justify-center text-[10px] sm:text-xs font-display font-bold text-foreground shadow-soft">
                    {index + 1}
                  </div>
                </div>
                <h3 className="text-sm sm:text-lg font-display font-bold text-foreground">{step.title}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonios" aria-label="Ejemplos de uso" className="scroll-mt-20 py-10 sm:py-20 md:py-28 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center space-y-3 sm:space-y-4 mb-8 sm:mb-16"
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.div variants={fadeUp} custom={0}>
              <Badge variant="outline" className="border-primary/20 text-primary">Ejemplos ilustrativos</Badge>
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-4xl md:text-5xl font-display font-extrabold text-foreground tracking-tight">
              Ideas para explorar la beta
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Casos ficticios para ilustrar las herramientas; no son testimonios ni resultados acreditados.
            </motion.p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                initials: "MR",
                name: "Explorar una idea",
                role: "Ejemplo ficticio · Tecnología",
                text: "Revisar hipótesis de mercado y riesgos antes de tomar decisiones, contrastando el análisis de IA con otras fuentes.",
                accent: "primary",
              },
              {
                initials: "LP",
                name: "Preparar los trámites",
                role: "Ejemplo ficticio · Consultoría",
                text: "Comprender el DUE y preparar preguntas para el Punto PAE o la notaría; la constitución se realiza por los canales oficiales.",
                accent: "secondary",
              },
              {
                initials: "JA",
                name: "Investigar financiación",
                role: "Ejemplo ficticio · Salud digital",
                text: "Localizar programas públicos y revisar sus requisitos en la convocatoria oficial, sin garantía de elegibilidad ni de concesión.",
                accent: "accent",
              },
            ].map((t, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <Card className="h-full rounded-2xl border border-border/50 hover:border-border transition-colors">
                  <CardContent className="p-5 sm:p-7 flex flex-col h-full">
                    <Quote className="text-muted-foreground/30 mb-3" size={24} />
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1">"{t.text}"</p>
                    <div className="flex items-center gap-3 pt-4 border-t border-border/50">
                      <div className={`w-10 h-10 rounded-full bg-${t.accent}/10 flex items-center justify-center flex-shrink-0`}>
                        <span className={`text-${t.accent} font-display font-bold text-sm`}>{t.initials}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-foreground">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section aria-label="Cumplimiento legal y confianza" className="py-10 sm:py-20 md:py-28 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center space-y-3 sm:space-y-4 mb-8 sm:mb-16"
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.div variants={fadeUp} custom={0}>
              <Badge variant="outline" className="border-primary/20 text-primary">Confianza</Badge>
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-4xl md:text-5xl font-display font-extrabold text-foreground tracking-tight">
              Orientación y fuentes oficiales
            </motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              { icon: GraduationCap, title: "Guías CIRCE", desc: "Información y enlaces a los canales oficiales, sin afiliación institucional" },
              { icon: Shield, title: "Trámites del usuario", desc: "La presentación del DUE y las gestiones se realizan en los portales oficiales" },
              { icon: Scale, title: "Información orientativa", desc: "Contrasta requisitos y normativa con fuentes oficiales y profesionales cualificados" }
            ].map((item, index) => (
              <motion.div key={index} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={index}>
                <Card className="text-center card-elevated rounded-2xl border border-border/50 h-full">
                  <CardContent className="p-5 sm:p-8">
                    <div className="w-11 h-11 sm:w-14 sm:h-14 bg-primary/10 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-5">
                      <item.icon className="text-primary" size={22} />
                    </div>
                    <h3 className="text-base sm:text-xl font-display font-bold text-foreground mb-2 sm:mb-3">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section aria-label="Preguntas frecuentes" className="py-10 sm:py-20 bg-card">
        <div className="max-w-3xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
            <motion.div variants={fadeUp} custom={0} className="text-center mb-8 sm:mb-12">
              <Badge variant="secondary" className="mb-3 sm:mb-4">
                <HelpCircle size={14} className="mr-1" /> FAQ
              </Badge>
              <h2 className="text-2xl sm:text-4xl font-display font-extrabold text-foreground tracking-tight mb-2 sm:mb-3">
                Preguntas Frecuentes
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
                Resolvemos las dudas más habituales sobre crear tu empresa en España con CreaFinancia.
              </p>
            </motion.div>
            <motion.div variants={fadeUp} custom={1}>
              <Accordion type="single" collapsible className="space-y-2">
                <AccordionItem value="faq-beta-limits" className="border rounded-xl px-4 bg-background">
                  <AccordionTrigger className="text-sm sm:text-base text-left font-semibold">¿Qué límites tiene la beta gratuita?</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                    Cada nuevo usuario puede crear 2 planes de negocio, 2 planes financieros y una empresa en total; borrar un recurso no recupera el cupo. Cada usuario dispone de 20 mensajes diarios compartidos entre los chats, con hasta 5 consultas con imágenes dentro de esos 20, y 2 generaciones de IA por documento de plan de negocio o financiero. Los cupos diarios se reinician a las 00:00 UTC; descargar PDFs ya generados no consume IA.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-1" className="border rounded-xl px-4 bg-background">
                  <AccordionTrigger className="text-sm sm:text-base text-left font-semibold">¿Qué es CreaFinancia y cómo funciona?</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                    CreaFinancia es una Aplicación Web Progresiva (PWA) que utiliza inteligencia artificial para ayudarte a crear tu empresa en España. Analiza la viabilidad de tu idea de negocio, te guía paso a paso en la constitución de tu sociedad y te conecta con ayudas públicas y subvenciones adaptadas a tu perfil.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-2" className="border rounded-xl px-4 bg-background">
                  <AccordionTrigger className="text-sm sm:text-base text-left font-semibold">¿El análisis de viabilidad es realmente gratuito?</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                    Sí. Necesitas una cuenta gratuita para realizar el análisis. Durante la beta, los tres niveles son gratuitos, sin tarjeta ni nuevos cobros. El uso de IA está sujeto a los cupos de la beta y el acceso no se convertirá automáticamente en una suscripción de pago.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-3" className="border rounded-xl px-4 bg-background">
                  <AccordionTrigger className="text-sm sm:text-base text-left font-semibold">¿Cómo me ayuda la IA a constituir mi empresa?</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                    La IA ofrece orientación sobre formas jurídicas, borradores de plan de negocio y guías DUE/CIRCE. Tú realizas los trámites por los canales oficiales; la aplicación no es un Punto PAE acreditado ni garantiza plazos o resultados.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-4" className="border rounded-xl px-4 bg-background">
                  <AccordionTrigger className="text-sm sm:text-base text-left font-semibold">¿Qué ayudas y subvenciones puedo encontrar?</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                    CreaFinancia busca y filtra ayudas ENISA, CDTI, subvenciones autonómicas, nacionales y europeas adaptadas a tu perfil empresarial, sector y ubicación. Ofrece información orientativa sobre requisitos y plazos, que debes confirmar en la convocatoria oficial. ENISA ofrece préstamos participativos, no subvenciones a fondo perdido. La compatibilidad estimada no acredita elegibilidad ni garantiza la concesión.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-5" className="border rounded-xl px-4 bg-background">
                  <AccordionTrigger className="text-sm sm:text-base text-left font-semibold">¿Es seguro introducir los datos de mi proyecto?</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                    Aplicamos medidas de seguridad y controles de acceso, sin garantía de seguridad absoluta. Los proveedores de alojamiento, correo e IA pueden tratar datos para operar las herramientas según la Política de Privacidad. No vendemos tus datos; evita aportar información personal innecesaria o sensible.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-6" className="border rounded-xl px-4 bg-background">
                  <AccordionTrigger className="text-sm sm:text-base text-left font-semibold">¿Puedo usar CreaFinancia desde el móvil?</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                    Sí, CreaFinancia es una aplicación web progresiva (PWA) totalmente optimizada para móvil. Puedes instalarla en tu smartphone como una app nativa y acceder a todas las funcionalidades desde cualquier dispositivo y lugar.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </motion.div>
            <motion.div variants={fadeUp} custom={7} className="text-center mt-6 sm:mt-8">
              <Button variant="link" className="text-primary hover:text-primary/80 font-semibold text-sm sm:text-base" onClick={() => navigate('/help-center')}>
                Ver más preguntas en el Centro de Ayuda →
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section aria-label="Llamada a la acción" className="py-10 sm:py-20 gradient-hero">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
            <motion.h2 variants={fadeUp} custom={0} className="text-2xl sm:text-4xl font-display font-extrabold text-foreground mb-3 sm:mb-4 tracking-tight">
              ¿Listo para emprender?
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-sm sm:text-lg text-muted-foreground mb-6 sm:mb-8 max-w-xl mx-auto">
              Analiza tu idea de negocio gratis y descubre su potencial con nuestra IA especializada.
            </motion.p>
            <motion.div variants={fadeUp} custom={2}>
              <Button size="lg" onClick={handleStartAnalysis} className="gradient-primary text-primary-foreground shadow-primary hover:shadow-xl transition-all h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-lg font-semibold rounded-xl group">
                <Rocket className="mr-2 group-hover:rotate-12 transition-transform" size={18} />
                Comenzar Análisis Gratuito
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* AI Chat Widget */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        <Suspense fallback={null}>
          <LandingChat onLoginRequired={handleLogin} />
        </Suspense>
      </div>

      </main>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>

      {showInstallGuide && (
        <Suspense fallback={null}>
          <InstallGuideDialog open={showInstallGuide} onOpenChange={setShowInstallGuide} />
        </Suspense>
      )}
    </div>
  );
}
